import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { ReactElement, useEffect } from 'react'

import SEO from '@/components/SEO'
import createOgImage from '@/lib/createOgImage'

import HorizontalScroll from '@/components/HorizontalScroll'
import Timeline from '@/components/Timeline'
import Button from '@/components/Button'
import SocialNetwork from '@/assets/social_network'
import {
  Container,
  HorizontalSection,
  Hobbies,
  Blur
} from '@/styles/pages/About'

import { useTheme } from 'styled-components'
import { FiArrowUpRight } from 'react-icons/fi'

const experiences = [
  {
    period: '2022 — hoje',
    company: 'Onebrain · alocado na Sympla',
    role: 'Senior Frontend Software Engineer',
    description:
      'Trabalho em discovery, páginas de evento, login e checkout, ajudando a evoluir jornadas de alto tráfego com foco em performance, acessibilidade e experiência.'
  },
  {
    period: '2024 — 2025',
    company: 'Vylex · MedCof',
    role: 'Software Engineer / Tech Lead',
    description:
      'Liderei melhorias de performance e dados e conduzi as principais decisões técnicas do MedCof Aulas para Android e iOS, do zero ao lançamento.'
  },
  {
    period: '2020 — 2024',
    company: 'Timeware · Consigaz, Linx, Banco Safra e Ultragaz',
    role: 'Software Engineer / Full-Stack Engineer',
    description:
      'Construí aplicações web, integrações e fluxos de negócio em diferentes domínios, incluindo o App Consigaz: Peça Seu Gás.'
  }
]

const projects = [
  {
    title: 'Papyrus SDK',
    type: 'Open source',
    description: 'SDK modular para PDF, EPUB e TXT em web e mobile.',
    url: 'https://solrachix.github.io/Papyrus/'
  },
  {
    title: 'PacketShield',
    type: 'Produto',
    description:
      'Firewall inteligente para Windows com regras por app e domínio.',
    url: 'https://packetshield.solrachix.com/'
  },
  {
    title: 'Papel Amassado',
    type: 'Produto',
    description:
      'Plataforma de leitura e escrita com foco em conteúdo e experiência.',
    url: 'https://papel-amassado.com/'
  },
  {
    title: 'Apps publicados',
    type: 'Android e iOS',
    description:
      'MedCof Aulas e App Consigaz, somando mais de 55 mil downloads.',
    url: 'https://github.com/solrachix'
  }
]

const hobbies = [
  {
    title: 'Desenvolvimento',
    description:
      'Sou apaixonado por tecnologia me apaixonei pelo mundo do desenvolvimento. Por isso estou praticando, estudando e aperfeiçoando as técnicas.',
    image: '/images/development.svg'
  },
  {
    title: 'Esportes',
    description:
      'Sou fã e praticante de alguns esportes específicos, tais como: Karate, Academia de Musculação e Queimada.',
    image: '/images/sport.svg'
  },
  {
    title: 'Livros',
    description: `Amo ler. Ler é viajar sem sair do lugar.
      Dentre as minhas obras favoritas está Senhor dos anéis, Harry Potter, Percy Jackson e todo o universo de Rick Riordan, Narnia e as obras incríveis do escritor brasileiro, eduardo spohr, como a trilogia Filhos do Eden e A Batalha do Apocalipse.`,
    image: '/images/reading.svg'
  },
  {
    title: 'Desenhos',
    description: `O desenho é a arte de representar.
      É um dos hobbies que eu mais amo. Gosto de desenhar no meu tempo livre.`,
    image: '/images/drawing.svg'
  },
  {
    title: 'Novas Tecnologias',
    description:
      'Estou sempre em busca de novos conhecimentos e desafios, principalmente quando se trata na área de tecnologia e desenvolvimento software.',
    image: '/images/new_technologies.svg'
  }
]

interface AboutStaticProps {
  ogImage: string
}

type AboutProps = InferGetStaticPropsType<GetStaticProps<AboutStaticProps>>

export default function About(props: AboutProps): ReactElement {
  const theme = useTheme().colors

  useEffect(() => {
    const layout = document.querySelector('#layout') as HTMLDivElement
    layout.style.backgroundColor = theme.background.dark
    return () => {
      layout.style.backgroundColor = theme.background.normal
    }
  }, [])

  return (
    <Container>
      <SEO
        title="Sobre mim"
        description="Carlos Miguel é Senior Software Engineer com mais de 8 anos de experiência em frontend, arquitetura e produto."
        image={props.ogImage}
      />

      <Blur
        style={{
          width: '80%',
          top: '0%',
          right: '6%',
          transform: 'translate(50%, -50%)',
          zIndex: 5
        }}
      />
      <Blur
        style={{
          left: '1px',
          // bottom: 0,
          transform: 'translate(-50%, 50%)'
        }}
      />

      <section>
        <div className="container">
          <div className="title-box">
            <h6 className="nick">@SOLRACHIX</h6>
            <h1 className="title">Carlos Miguel</h1>
            <p className="hero-role">
              Senior Software Engineer · Frontend, Architecture &amp; Product
            </p>
            <p className="paragraph hero-copy">
              Construo produtos digitais que funcionam, escalam e têm
              personalidade — do primeiro protótipo a produtos usados em escala.
            </p>
            <div className="hero-actions">
              <a href="/projects">
                Ver projetos <FiArrowUpRight />
              </a>
              <a
                href="https://www.linkedin.com/in/solrachix/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn <FiArrowUpRight />
              </a>
            </div>
            <div className="metrics" aria-label="Números da carreira">
              <div>
                <strong>8+</strong>
                <span>anos de experiência</span>
              </div>
              <div>
                <strong>55k+</strong>
                <span>downloads em apps</span>
              </div>
              <div>
                <strong>47</strong>
                <span>repositórios públicos</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <h1 className="titleSection">Sobre</h1>
          <p className="paragraph">
            Comecei com curiosidade, um computador de tubo e a vontade de
            entender como as coisas funcionavam. Da Etec e dos primeiros
            projetos em C++ e VBA até produtos usados em escala, fui juntando
            engenharia, design e uma obsessão saudável por experiências
            melhores.
          </p>
          <div className="experience-grid">
            {experiences.map(experience => (
              <article key={experience.company} className="experience-card">
                <span>{experience.period}</span>
                <h3>{experience.role}</h3>
                <strong>{experience.company}</strong>
                <p>{experience.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <Blur
            style={{
              width: '120%',
              top: '50%',
              transform: 'translate(-50%, 50%)'
            }}
          />
          <div className="content">
            <h1 className="subTitleSection">Linha do tempo</h1>
          </div>
          <HorizontalSection>
            <HorizontalScroll>
              <div className="container">
                <Timeline />
              </div>
            </HorizontalScroll>
          </HorizontalSection>
          {/* <div className="divider"></div> */}
          <Blur
            style={{
              right: '1px',
              filter: 'blur(100px)',
              transform: 'translate(50%, 0%)'
            }}
          />
        </div>
      </section>

      <section>
        <div className="container">
          <h1 className="subTitleSection">
            Projetos que carregam minha assinatura
          </h1>
          <div className="project-grid">
            {projects.map(project => (
              <a
                key={project.title}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="project-card"
              >
                <span>{project.type}</span>
                <h3>
                  {project.title} <FiArrowUpRight />
                </h3>
                <p>{project.description}</p>
              </a>
            ))}
          </div>
          <h1 className="subTitleSection hobbies-title">Hobbies</h1>
          <Blur />
          <Hobbies>
            {hobbies.map((hobbie, index) => {
              index++
              return (
                <div key={`${hobbie.title}-${index}`} className="hobbie">
                  <header className="hobbie-header">
                    <strong>{hobbie.title}</strong>
                    <span>{index < 10 ? `0${index}` : index}</span>
                  </header>
                  <div className="hobbie-content">
                    <img src={hobbie.image} alt={hobbie.title} />
                    <p>{hobbie.description} </p>
                  </div>
                </div>
              )
            })}
          </Hobbies>
        </div>
      </section>

      <section>
        <div className="container">
          <label>UM POUCO MAIS DO MEU TRABALHO</label>
          <h1 className="titleSection">Minhas redes</h1>
          <p className="paragraph">
            Cada rede mostra um lado diferente do meu trabalho: código e open
            source no GitHub, bastidores e design no Instagram, e trajetória
            profissional no LinkedIn. Passe o mouse pelos itens da ilustração.
          </p>
          <Blur
            style={{
              left: '50%',
              transform: 'translate(-50%, 50%)',
              opacity: 0.2,
              filter: 'blur(80px)'
            }}
          />
          <SocialNetwork className="social-network" />
        </div>
      </section>

      <section>
        <div className="container">
          <label>UM POUCO MAIS DO MEU TRABALHO</label>
          <h1 className="titleSection">
            Pronto para começarmos um projeto juntos?
          </h1>
          <p className="paragraph">
            Se você gostou do meu trabalho e deseja tirar sua ideia do papel e
            dar um boost nos seus serviços, então é só cair pra dentro! Teve
            algum dúvida? Por favor me mande uma DM.
          </p>
          <Button
            type="button"
            text="Converse comigo"
            onClick={() => {
              window.open('https://www.instagram.com/solrachix', '_blank')
            }}
          />
        </div>
      </section>
    </Container>
  )
}

export const getStaticProps: GetStaticProps<AboutStaticProps> = async () => {
  const ogImage = await createOgImage({
    title: 'Sobre mim'
  })

  return {
    revalidate: 86400,
    props: {
      ogImage
    }
  }
}
